import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';

import { Store } from '@ngrx/store';
import { ROUTER_NAVIGATED } from '@ngrx/router-store';
import { Actions, createEffect, ofType } from '@ngrx/effects';

import { of } from 'rxjs';
import { catchError, filter, map, switchMap, tap, withLatestFrom } from 'rxjs/operators';

import { ToastrService } from 'ngx-toastr';

import * as moment from 'moment';

import { CustomRouterNavigatedAction } from '../../../core/custom-route-serializer';
import { DASHBOARD_ROUTES } from '../../dashboard.routes';
import { AuthErrorResponse } from '../../../models/auth-error-response';
import * as studyActions from './study.actions';
import * as interventionActions from '../intervention/intervention.actions';
import * as studyResearcherActions from '../study-researcher/study-researcher.actions';
import * as moduleActions from '../module/module.actions';
import * as categoryActions from '../category/category.actions';
import { StudyService } from '../../services/study/study.service';
import { DashboardCamelCasePipe } from '../../../core/pipes/dashboard-camel-case/dashboard-camel-case.pipe';
import { setStudyStatus } from '../../studies/helpers/status-helper';
import { addTransformedInterventions } from '../../studies/helpers/intervention-helper';
import { Study } from 'src/app/models/study';
import { DashboardSnakeCasePipe } from '../../../core/pipes/dashboard-snake-case/dashboard-snake-case.pipe';
import { AppState } from '../../../state';
import { selectSelectedStudy } from './study.selectors';
import { STUDIES_ROUTES } from '../../studies/studies.routes';
import { transformLanguageCodesToLanguageEntities } from 'src/app/core/languages-helper';
import {initializeUpload, uploadRequest} from "../upload/upload.actions";
import {UploadKeys} from "../../../models/upload";

export function transformStudyFromApi(study): Study {
  const languagesArray = study.languages.split(',');
  const newTranslations = [];
  for (const translation of study.translations) {
    if (languagesArray.find((langCode) => langCode === translation.language)) {
      newTranslations.push(translation);
    }
  }
  return {
    ...study,
    status: setStudyStatus(study.startsAt, study.endsAt),
    startsAt: moment(study.startsAt),
    endsAt: moment(study.endsAt),
    languages: languagesArray,
    translations: newTranslations,
    interventions: study.interventions ? addTransformedInterventions(study) : [],
  };
}

@Injectable()
export class StudyEffects {
  studiesRoute$ = createEffect(() =>
    this.actions$.pipe(
      ofType<CustomRouterNavigatedAction>(ROUTER_NAVIGATED),
      filter((route: CustomRouterNavigatedAction) => {
        return (route.payload.routerState.path === DASHBOARD_ROUTES.STUDIES);
      }),
      map(() => studyActions.getStudies())
    )
  );

  studiesOverviewRoute$ = createEffect(() =>
    this.actions$.pipe(
      ofType<CustomRouterNavigatedAction>(ROUTER_NAVIGATED),
      filter((route: CustomRouterNavigatedAction) => {
        return (route.payload.routerState.path === DASHBOARD_ROUTES.ROOT);
      }),
      map(() => studyActions.getStudiesForOverview())
    )
  );

  studyRoute$ = createEffect(() =>
    this.actions$.pipe(
      ofType<CustomRouterNavigatedAction>(ROUTER_NAVIGATED),
      filter(
        (route: CustomRouterNavigatedAction) =>
          route.payload.routerState.url.includes(DASHBOARD_ROUTES.STUDIES) &&
          route.payload.routerState.path.startsWith(`:id`)
      ),
      withLatestFrom(this.store.select(selectSelectedStudy)),
      switchMap(([action, selectedStudy]) => {
        if (!selectedStudy || action?.payload?.routerState?.path?.endsWith(STUDIES_ROUTES.HOME)) {
          return [
            studyActions.selectStudy({
              studyId: action.payload.routerState.params.id,
            }),
          ];
        }
        return [
          interventionActions.setSelectedIntervention({
            selectedIntervention: null,
          }),
          moduleActions.setSelectedModule({ selectedModule: null }),
        ];
      })
    )
  );

  // TODO: move from study effects
  settingsRoute$ = createEffect(() =>
    this.actions$.pipe(
      ofType<CustomRouterNavigatedAction>(ROUTER_NAVIGATED),
      filter((route: CustomRouterNavigatedAction) =>
        route.payload.routerState.url.startsWith(`/${DASHBOARD_ROUTES.SETTINGS}`)
      ),
      switchMap(() => [
        studyActions.openSideNav({ navType: 'settings' }),
        studyActions.setSelectedStudy({ selectedStudy: null }),
      ])
    )
  );

  createStudyRoute$ = createEffect(() =>
    this.actions$.pipe(
      ofType<CustomRouterNavigatedAction>(ROUTER_NAVIGATED),
      filter((route: CustomRouterNavigatedAction) =>
        route.payload.routerState.url.startsWith(`/${DASHBOARD_ROUTES.STUDIES}/${STUDIES_ROUTES.CREATE_STUDY}`)
      ),
      map(() => studyActions.openSideNav({ navType: 'create' }))
    )
  );

  getStudies$ = createEffect(() =>
    this.actions$.pipe(
      ofType(studyActions.getStudies),
      switchMap(() => {
        return this.studyService.getStudies().pipe(
          map((studies) => {
            const transformedStudies = [];
            for (const study of studies) {
              const transformedStudy: Study = this.dashboardCamelCasePipe.keysToCamelCase(study);
              if (!transformedStudy.pivot || (transformedStudy.pivot && !transformedStudy.pivot.blocked)) {
                transformedStudy.status = setStudyStatus(transformedStudy.startsAt, transformedStudy.endsAt);
                transformedStudies.push(transformStudyFromApi(transformedStudy));
              }
            }
            return studyActions.getStudiesSuccess({
              studies: transformedStudies,
            });
          }),
          catchError((err: AuthErrorResponse) => of(studyActions.getStudiesError({ err })))
        );
      })
    )
  );

  getStudiesForOverview$ = createEffect(() =>
    this.actions$.pipe(
      ofType(studyActions.getStudiesForOverview),
      switchMap(() => {
        return this.studyService.getStudiesForOverview(1).pipe(
          map((studies) => {
            const transformedStudies = [];
            for (const study of studies.data) {
              const transformedStudy: Study = this.dashboardCamelCasePipe.keysToCamelCase(study);
              if (!transformedStudy.pivot || (transformedStudy.pivot && !transformedStudy.pivot.blocked)) {
                transformedStudy.status = setStudyStatus(transformedStudy.startsAt, transformedStudy.endsAt);
                transformedStudies.push(transformStudyFromApi(transformedStudy));
              }
            }
            return studyActions.getStudiesSuccess({
              studies: transformedStudies,
            });
          }),
          catchError((err: AuthErrorResponse) => of(studyActions.getStudiesError({ err })))
        );
      })
    )
  );

  getStudiesError$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(studyActions.getStudiesError),
        tap(({ err }) => this.toastrService.error(err.error.error))
      ),
    { dispatch: false }
  );

  openCreateStudyPageWithDetails$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(studyActions.openCreateStudyPageWithDetails),
        tap(({ studyType }) =>
          this.router.navigate([`/${DASHBOARD_ROUTES.STUDIES}/${STUDIES_ROUTES.CREATE_STUDY}`], {
            queryParams: { type: studyType },
          })
        )
      ),
    { dispatch: false }
  );

  createStudy$ = createEffect(() =>
    this.actions$.pipe(
      ofType(studyActions.createStudy),
      switchMap(({ study, uuid }) => {
        const transformedStudyApi = this.dashboardSnakeCasePipe.keysToSnakeCase(study);
        return this.studyService.createStudy(transformedStudyApi).pipe(
          switchMap((newStudy) => {
            const newStudyCamelCase = this.dashboardCamelCasePipe.keysToCamelCase(newStudy);
            if (newStudyCamelCase?.imagePath?.includes('blob:http')) {
              return [
                studyActions.uploadStudyImage( {
                  newStudy: transformStudyFromApi(newStudyCamelCase),
                  uuid
                }),
                studyActions.selectStudy({ studyId: newStudy.id }),
              ];
            } else {
              return [
                studyActions.createStudySuccess({
                  newStudy: transformStudyFromApi(newStudyCamelCase),
                }),
                studyActions.selectStudy({ studyId: newStudy.id }),
              ];
            }
          }),
          catchError((err: HttpErrorResponse) => of(studyActions.createStudyError({ err })))
        );
      })
    )
  );

  uploadStudyImage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(studyActions.uploadStudyImage),
      tap(({ newStudy, uuid }) => {
        sessionStorage.setItem('newStudy', JSON.stringify(newStudy));
        if (newStudy.imagePath.includes('blob:http')) {
          fetch(newStudy.imagePath).then(r => r.blob()).then((blobFile) => {
            const type = blobFile.type;
            const extension = '.' + type.split('/')[1];
            return new File([blobFile], uuid + extension, { type })
          }).then((file) => {
            const idsForUpload = {
              studyId: newStudy?.id,
            };
            this.store.dispatch(
              uploadRequest({
                file,
                uuid,
                keyForUpload: UploadKeys.UPLOAD_FROM_STUDY,
                idsForUpload,
              })
            );
          })
        }
      })
    ),
    { dispatch: false }
  );

  createStudySuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(studyActions.createStudySuccess),
      map(({ newStudy }) => {
        this.toastrService.success(`Successfully created study ${newStudy.translations[0].translation}`);
        this.router.navigate([`${DASHBOARD_ROUTES.STUDIES}/${newStudy.id}/${STUDIES_ROUTES.HOME}`])
          .then(() => {
            window.location.reload();
          });
        return studyActions.openSideNav({ navType: newStudy.type });
      })
    )
  );

  createStudyError$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(studyActions.createStudyError),
        tap(({ err }) => this.toastrService.error(`Error in creating study ${err.error}`))
      ),
    { dispatch: false }
  );

  selectStudy$ = createEffect(() =>
    this.actions$.pipe(
      ofType(studyActions.selectStudy),
      switchMap(({ studyId }) => {
        return this.studyService.getStudyById(studyId).pipe(
          map((study) => {
            const studyCamelCase = this.dashboardCamelCasePipe.keysToCamelCase(study);
            return studyActions.selectStudySuccess({
              selectedStudy: transformStudyFromApi(studyCamelCase),
            });
          }),
          catchError((err: HttpErrorResponse) => of(studyActions.selectStudyError({ err })))
        );
      })
    )
  );

  selectStudySuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(studyActions.selectStudySuccess),
      switchMap(({ selectedStudy }) => {
        const transformedLanguages = transformLanguageCodesToLanguageEntities(selectedStudy.languages);
        return [
          studyActions.openSideNav({ navType: selectedStudy.type }),
          studyActions.addStudySupportedLanguages({
            supportedLanguages: transformedLanguages,
          }),
          selectedStudy?.interventions
            ? interventionActions.getInterventionsSuccess({
                interventions: selectedStudy.interventions,
              })
            : studyActions.noopAction(),
          selectedStudy?.modules
            ? moduleActions.getModulesSuccess({
                modules: selectedStudy?.modules,
              })
            : studyActions.noopAction(),
          selectedStudy?.categories
            ? categoryActions.getCategoriesSuccess({ categories: selectedStudy?.categories })
            : studyActions.noopAction,
          selectedStudy?.researchers
            ? studyResearcherActions.getStudyResearchers({
                researchers: selectedStudy.researchers,
              })
            : studyActions.noopAction(),
        ];
      })
    )
  );

  selectStudyError$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(studyActions.selectStudyError),
        tap(({ err }) => {
          this.toastrService.error(err.error?.message, 'Error in fetching study details');
          this.router.navigate([DASHBOARD_ROUTES.STUDIES]);
        })
      ),
    { dispatch: false }
  );

  updateStudy$ = createEffect(() =>
    this.actions$.pipe(
      ofType(studyActions.updateStudy),
      switchMap(({ study, skipFlash }) => {
        const transformedStudyApi = this.dashboardSnakeCasePipe.keysToSnakeCase(study);
        return this.studyService.updateStudy(transformedStudyApi).pipe(
          map((newStudy) => {
            const newStudyCamelCase = this.dashboardCamelCasePipe.keysToCamelCase(newStudy);
            if (!skipFlash) {
              return studyActions.updateStudySuccess({
                updatedStudy: transformStudyFromApi(newStudyCamelCase),
              });
            } else {
              return studyActions.uploadStudyImageSuccess({
                uploadedComplete: true
              });
            }
          }),
          catchError((err: HttpErrorResponse) => of(studyActions.updateStudyError({ err })))
        );
      })
    )
  );

  updateStudySuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(studyActions.updateStudySuccess),
        tap(({ updatedStudy }) => {
          this.toastrService.success(`Successfully updated study ${updatedStudy.translations[0].translation}`);
        })
      ),
    { dispatch: false }
  );

  updateStudyError$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(studyActions.updateStudyError),
        tap(({ err }) => this.toastrService.error(`Error in updating study ${err.error.error}`))
      ),
    { dispatch: false }
  );

  deleteStudy$ = createEffect(() =>
    this.actions$.pipe(
      ofType(studyActions.deleteStudy),
      switchMap(({ study }) => {
        const transformedStudyApi = this.dashboardSnakeCasePipe.keysToSnakeCase(study);
        return this.studyService.deleteStudy(transformedStudyApi).pipe(
          map(() => studyActions.deleteStudySuccess({ deletedStudy: study })),
          catchError((err: HttpErrorResponse) => of(studyActions.deleteStudyError))
        );
      })
    )
  );

  deleteStudySuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(studyActions.deleteStudySuccess),
        tap(() => this.toastrService.success(`Study deleted!`))
      ),
    { dispatch: false }
  );

  deleteStudyError$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(studyActions.deleteStudyError),
        tap(({ err }) => this.toastrService.error(`Error deleting study ${err.error}`))
      ),
    { dispatch: false }
  );

  duplicateStudy$ = createEffect(() =>
    this.actions$.pipe(
      ofType(studyActions.duplicateStudy),
      switchMap(({ study }) => {
        const transformedStudyApi = this.dashboardSnakeCasePipe.keysToSnakeCase(study);
        return this.studyService.duplicateStudy(transformedStudyApi).pipe(
          map((newStudy) => {
            const newStudyCamelCase = this.dashboardCamelCasePipe.keysToCamelCase(newStudy);
            return studyActions.duplicateStudySuccess({
              duplicatedStudy: transformStudyFromApi(newStudyCamelCase),
            });
          }),
          catchError((err: HttpErrorResponse) => of(studyActions.deleteStudyError))
        );
      })
    )
  );

  duplicateStudySuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(studyActions.duplicateStudySuccess),
        tap(() => this.toastrService.success(`Study duplicated!`))
      ),
    { dispatch: false }
  );

  duplicateStudyError$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(studyActions.duplicateStudyError),
        tap(({ err }) => this.toastrService.error(`Unable to duplicate study ${err.error}`))
      ),
    { dispatch: false }
  );

  constructor(
    private actions$: Actions,
    private store: Store<AppState>,
    private studyService: StudyService,
    private toastrService: ToastrService,
    private router: Router,
    private dashboardCamelCasePipe: DashboardCamelCasePipe,
    private dashboardSnakeCasePipe: DashboardSnakeCasePipe
  ) {}
}
